import { BAOCustomElement } from '@bao/custom-element'
import { MediaBreakpoints } from '@bao/media'
import { debounce, isInViewport } from '@bao/utils'

class StoreLocator extends BAOCustomElement() {
  constructor () {
    super()

    this._country = this.dataset.country || ''
    this._store = this.dataset.store || ''
  }

  connectedCallback () {
    super.connectedCallback()

    this.updateScrollOverlay()
  }

  setupListeners () {
    super.setupListeners()

    this.debouncedResize = debounce(() => {
      this.updateScrollOverlay()
    }, 240)

    this.listeners.add(window, 'resize', this.debouncedResize)

    this.listeners.add(this, 'click', this.handleClick.bind(this))

    this.listeners.add(
      this.els.scroll.element,
      'scroll',
      this.handleStoresListScroll.bind(this),
    )
  }

  handleClick (event) {
    const countryButtonEl = event.target.closest(
      this.els.countryButton.selector,
    )

    if (countryButtonEl) {
      this.country = countryButtonEl.dataset.country
      return
    }

    const directionsEl = event.target.closest(this.els.directions.selector)
    if (directionsEl) return

    const storeButtonEl = event.target.closest(this.els.storeButton.selector)

    if (storeButtonEl) {
      this.store = storeButtonEl.dataset.store

      if (MediaBreakpoints.isMd) return

      let scrollToEl = storeButtonEl

      if (MediaBreakpoints.isXsViewport) {
        const storeMapEl = this.els.storeMap.elements.find((element) => {
          return (
            element.dataset.country === this.country &&
            element.dataset.store === this.store
          )
        })

        if (storeMapEl) {
          scrollToEl = storeMapEl
        }
      }

      const prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      ).matches

      setTimeout(
        () => {
          if (isInViewport(scrollToEl)) return

          scrollToEl.scrollIntoView({
            behavior: prefersReducedMotion ? 'instant' : 'smooth',
            block: 'start',
            inline: 'start',
          })
        },
        prefersReducedMotion ? 100 : 500,
      )
    }
  }

  get country () {
    return this._country
  }

  set country (value) {
    this._country = value

    this.els.countryButton.elements.forEach((countryButtonEl) => {
      const isActive = countryButtonEl.dataset.country === this.country
      countryButtonEl.setAttribute('aria-expanded', isActive)
    })

    let newStore = ''

    this.els.storeButton.elements.forEach((storeButtonEl) => {
      const isActive = storeButtonEl.dataset.country === this.country

      if (isActive && !newStore) {
        newStore = storeButtonEl.dataset.store
      }

      storeButtonEl.setAttribute('aria-hidden', !isActive)
    })

    this.els.storeDetail.elements.forEach((storeDetailEl) => {
      const isActive = storeDetailEl.dataset.country === this.country
      storeDetailEl.setAttribute('aria-hidden', !isActive)
    })

    this.els.storeMap.elements.forEach((storeMapEl) => {
      const isActive = storeMapEl.dataset.country === this.country
      storeMapEl.setAttribute('aria-hidden', !isActive)
    })

    this.updateScrollOverlay()

    /* Update active store to first store in country */
    this.store = newStore
  }

  get store () {
    return this._store
  }

  set store (value) {
    this._store = value

    this.els.storeButton.elements.forEach((storeButtonEl) => {
      const isActive =
        storeButtonEl.dataset.country === this.country &&
        storeButtonEl.dataset.store === this.store

      storeButtonEl.setAttribute('aria-selected', isActive)
    })

    this.els.storeDetail.elements.forEach((storeDetailEl) => {
      const isActive =
        storeDetailEl.dataset.country === this.country &&
        storeDetailEl.dataset.store === this.store

      storeDetailEl.setAttribute('aria-selected', isActive)
    })

    this.els.storeMap.elements.forEach((storeMapEl) => {
      const isActive =
        storeMapEl.dataset.country === this.country &&
        storeMapEl.dataset.store === this.store

      storeMapEl.setAttribute('aria-selected', isActive)
    })
  }

  handleStoresListScroll () {
    const maxScroll =
      this.els.scroll.element.scrollHeight -
      this.els.scroll.element.offsetHeight

    const scrollProgress = this.els.scroll.element.scrollTop / maxScroll

    if (scrollProgress < 0.9) {
      this.els.overlay.element.style.opacity = 1
      return
    }

    const opacity = (1 - scrollProgress) * 10
    this.els.overlay.element.style.opacity = opacity
  }

  updateScrollOverlay () {
    this.els.overlay.element.style.opacity = 1

    this.els.overlay.element.classList.toggle(
      'sec-StoreLocator_StoresOverlay-active',
      this.els.scroll.element.scrollHeight > 620,
    )
  }
}

if (!customElements.get('store-locator')) {
  customElements.define('store-locator', StoreLocator)
}
